import React, { useState, useEffect, useMemo, forwardRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Slide from "@mui/material/Slide";
import GenerateFormModel from "./GenerateFormModel/GenerateFormModel";
import {
  fetchGeneratedFormsList,
  Generated_forms_downloadDataInput,
  Generated_forms_downloadZip,
  deleteGeneratedFormById,
  Generated_forms_viewDataInput,
  Generated_forms_viewById,
  releaseGeneratedFormById,
  releaseGeneratedFormsBulk,
} from "../../../../services/register_redux/register_actions/generateForm.action";
import { getUnits } from "../../../../services/global_redux/globale_actions/Unit.action";
import { getClients } from "../../../../services/global_redux/globale_actions/Client.action";
import mainicon from "../../../../assets/register_assets/generatedForms/mainIcon.png";
import Branch_id_icon from "../../../../assets/register_assets/generatedForms/Branch_id_icon.png";
import Client_id_icon from "../../../../assets/register_assets/generatedForms/Client_id_icon.png";
import Current_Month_icon from "../../../../assets/register_assets/generatedForms/Current_Month_icon.png";
import GeneratedFormPdfPreview from "./GeneratedFormPdfPreview";
import { FaRegFileAlt, FaRegCheckCircle } from "react-icons/fa";
import { normalizeGeneratedKind } from "./GeneratedFormKindToggle";
import "./Generated_Forms.css";

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function clientDownloadErrorMessage(message, fallback = "Download failed") {
  const text = String(message || "").toLowerCase();
  if (text.includes("file not found") || text.includes("no file found")) {
    return "This document isn’t available to download right now.";
  }
  return (typeof message === "string" && message.trim()) ? message : fallback;
}

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function toMonthFirstDay(isoDateStr) {
  if (!isoDateStr) return "";
  const s = String(isoDateStr).trim();
  if (/^\d{4}-\d{2}$/.test(s)) return `${s}-01`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s.slice(0, 7)}-01`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function toYearMonth(value) {
  if (!value) return "";
  const s = String(value).trim();
  const iso = s.match(/^(\d{4}-\d{2})/);
  if (iso) return iso[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthsFromGeneratedRows(rows) {
  return [...new Set(
    (Array.isArray(rows) ? rows : [])
      .map((row) => toYearMonth(row?.current_month))
      .filter(Boolean)
  )].sort();
}

function formatMonthLabel(isoDateStr) {
  if (!isoDateStr) return "";
  const s = String(isoDateStr).trim();
  const match = s.match(/^(\d{4})-(\d{2})/);
  if (match) {
    const monthName = MONTH_NAMES[parseInt(match[2], 10) - 1];
    return monthName ? `${monthName} ${match[1]}` : s;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

function sameMonth(rowDate, filterDate) {
  if (!rowDate || !filterDate) return false;
  const a = String(rowDate).slice(0, 10);
  const b = String(filterDate).slice(0, 10);
  return a === b || a.slice(0, 7) === b.slice(0, 7);
}

function idsMatch(a, b) {
  return String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
}

function getSessionBaseRole() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "{}")?.base_role || "";
  } catch {
    return "";
  }
}

function getAssignedUnitIds(clientId) {
  try {
    const mapped = JSON.parse(
      JSON.parse(sessionStorage.getItem("user") || "{}").client_and_unit || "[]"
    );
    const entry = (Array.isArray(mapped) ? mapped : []).find((item) =>
      idsMatch(item?.client_id, clientId)
    );
    return (entry?.unit_id || []).map((id) => String(id).trim()).filter(Boolean);
  } catch {
    return [];
  }
}

function unitLocation(unit) {
  return String(unit?.Location_City || unit?.location_city || "").trim();
}

function orgOptionLabel(client) {
  if (!client) return "";
  const id = String(client.client_id || "").trim();
  const name = String(client.client_name || "").trim();
  if (id && name) return `${id} - ${name}`;
  return id || name;
}

function branchOptionLabel(unit) {
  const name = String(unit?.unit_name || "").trim();
  const location = unitLocation(unit);
  const id = String(unit?.unit_id || "").trim();
  let place = "";
  if (name && location) place = `${name} (${location})`;
  else place = name || location || "";
  if (id && place) return `${id} - ${place}`;
  return id || place;
}

function isFormReleased(row) {
  return row?.is_released === true || row?.is_released === 1 || row?.is_released === "1";
}

function withReleaseFields(rows, client_id, unit_id, date) {
  return (Array.isArray(rows) ? rows : [])
    .filter(
      (row) =>
        idsMatch(row.client_id, client_id) &&
        idsMatch(row.unit_id, unit_id) &&
        sameMonth(row.current_month, date)
    )
    .map((row) => {
      const released = isFormReleased(row);
      return {
        ...row,
        is_released: released,
        release_status: released ? "Released" : "Not released",
      };
    });
}

function GeneratedFormList({ setLoading, showSnackbar }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isClient =
    JSON.parse(sessionStorage.getItem("user") || "{}")?.base_role === "Client";
  const [searchParams, setSearchParams] = useSearchParams();

  const client_id = searchParams.get("client_id") || "";
  const unit_id = searchParams.get("unit_id") || "";
  const date = searchParams.get("date") || "";
  const kind = isClient ? "register" : normalizeGeneratedKind(searchParams.get("kind"));
  const isReturn = kind === "return";
  const itemNoun = isReturn ? "return" : "form";

  const [getData, setGetData] = useState([]);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewUnavailable, setPreviewUnavailable] = useState(false);
  const [openReleaseAlert, setOpenReleaseAlert] = useState(false);
  const [releaseRows, setReleaseRows] = useState([]);
  const [clientRecord, setClientRecord] = useState(null);
  const [unitOptions, setUnitOptions] = useState([]);
  const [allowedMonths, setAllowedMonths] = useState([]);
  const [zipBusy, setZipBusy] = useState(false);

  const selectedYm = toYearMonth(date);
  const selectedYear = selectedYm.slice(0, 4);
  const selectedMonth = selectedYm.slice(5, 7);

  const yearOptions = useMemo(
    () => [...new Set(allowedMonths.map((ym) => ym.slice(0, 4)))].sort((a, b) => Number(b) - Number(a)),
    [allowedMonths]
  );
  const monthOptions = useMemo(
    () => allowedMonths.filter((ym) => ym.startsWith(`${selectedYear}-`)).map((ym) => ym.slice(5, 7)),
    [allowedMonths, selectedYear]
  );

  const orgName = clientRecord?.client_name || client_id;
  const orgLabel = orgOptionLabel(clientRecord) || client_id;

  const header = [
    { key: "client_id", value: "Organization ID", input: "text", readOnly: true, icon: Client_id_icon, dataIcon: false, highlightName: false },
    { key: "unit_id", value: "Branch ID", input: "text", readOnly: true, icon: Branch_id_icon, dataIcon: false, highlightName: false },
    { key: "current_month", value: "Current Month", input: "date", icon: Current_Month_icon, dataIcon: false, highlightName: true },
    { key: "form_type", value: isReturn ? "Return Type" : "Form Type", input: "text", faIcon: FaRegFileAlt, dataIcon: false, highlightName: false },
    { key: "release_status", value: "Status", input: "text", faIcon: FaRegCheckCircle, dataIcon: false, highlightName: false },
    { key: "form_path", value: isReturn ? "Download Generated Return" : "Download Generated Form", input: "text", dataIcon: false, highlightName: false },
  ];

  const updateListParams = (nextUnit, nextDate, nextKind = kind) => {
    const next = {
      client_id,
      unit_id: nextUnit || unit_id,
      date: toMonthFirstDay(nextDate || date),
    };
    if (normalizeGeneratedKind(nextKind) === "return") next.kind = "return";
    setSearchParams(next);
  };

  useEffect(() => {
    if (!client_id || !unit_id || !date) {
      showSnackbar("Missing filters. Go back and select client, unit, and date.", "warning");
      navigate(kind === "return" ? "/generated-form?kind=return" : "/generated-form");
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await dispatch(fetchGeneratedFormsList({ client_id, unit_id, kind }));
        if (cancelled) return;

        if (!response.success) {
          showSnackbar(response.message || (isReturn ? "Failed to load returns." : "Failed to load forms."), "error");
          setGetData([]);
          setAllowedMonths([]);
          return;
        }

        const rows = (Array.isArray(response.data) ? response.data : []).filter(
          (row) => idsMatch(row.client_id, client_id) && idsMatch(row.unit_id, unit_id)
        );
        setAllowedMonths(monthsFromGeneratedRows(rows));
        const filtered = withReleaseFields(rows, client_id, unit_id, date);
        setGetData(filtered);
        if (filtered.length === 0) {
          showSnackbar(
            isReturn
              ? "No generated returns for this branch and month."
              : "No records for selected client, unit, and date.",
            "warning"
          );
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          showSnackbar(isReturn ? "Failed to load generated returns." : "Failed to load generated forms.", "error");
          setGetData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client_id, unit_id, date, kind, dispatch]);

  useEffect(() => {
    if (!client_id) return;
    let cancelled = false;
    (async () => {
      try {
        const [clientsRes, unitsRes] = await Promise.all([
          dispatch(getClients()),
          dispatch(getUnits(client_id)),
        ]);
        if (cancelled) return;
        const match = (Array.isArray(clientsRes.data) ? clientsRes.data : []).find(
          (c) => String(c.client_id) === String(client_id)
        );
        setClientRecord(match || null);
        const units = Array.isArray(unitsRes.data) ? unitsRes.data : [];
        const assigned = getAssignedUnitIds(client_id);
        const baseRole = getSessionBaseRole();
        setUnitOptions(
          units.filter((u) => {
            if (u.client_id && !idsMatch(u.client_id, client_id)) return false;
            if (baseRole === "SuperAdmin" || !assigned.length) return true;
            return assigned.some((id) => idsMatch(id, u.unit_id));
          })
        );
      } catch {
        if (!cancelled) {
          setClientRecord(null);
          setUnitOptions([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [client_id, dispatch]);

  const reloadList = async () => {
    setLoading(true);
    try {
      const response = await dispatch(fetchGeneratedFormsList({ client_id, unit_id, kind }));
      if (!response.success) {
        setGetData([]);
        return;
      }
      const rows = (Array.isArray(response.data) ? response.data : []).filter(
        (row) => idsMatch(row.client_id, client_id) && idsMatch(row.unit_id, unit_id)
      );
      setAllowedMonths(monthsFromGeneratedRows(rows));
      setGetData(withReleaseFields(rows, client_id, unit_id, date));
    } finally {
      setLoading(false);
    }
  };

  const handleBranchChange = (nextUnit) => {
    if (!nextUnit || idsMatch(nextUnit, unit_id)) return;
    updateListParams(nextUnit, date);
  };

  const handleYearChange = (year) => {
    const monthsForYear = allowedMonths
      .filter((ym) => ym.startsWith(`${year}-`))
      .map((ym) => ym.slice(5, 7));
    const nextMonth = monthsForYear.includes(selectedMonth) ? selectedMonth : monthsForYear[0];
    if (!year || !nextMonth) return;
    updateListParams(unit_id, `${year}-${nextMonth}-01`);
  };

  const handleMonthChange = (month) => {
    if (!selectedYear || !month) return;
    updateListParams(unit_id, `${selectedYear}-${month}-01`);
  };

  const handleDownloadClick = async (id) => {
    setLoading(true);
    try {
      const response = await dispatch(Generated_forms_downloadDataInput(id));
      if (response.success) showSnackbar(response.message, "success");
      else {
        showSnackbar(
          isClient ? clientDownloadErrorMessage(response.message) : (response.message || "Download failed"),
          isClient ? "warning" : "error"
        );
      }
    } catch {
      showSnackbar("An error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleZipDownload = async (ids) => {
    const allowed = new Set(getData.map((row) => String(row.id)));
    const zipIds = (ids || []).filter((id) => id != null && allowed.has(String(id)));
    if (!zipIds.length) {
      showSnackbar("select the row", "warning");
      return;
    }
    const zipName = `${client_id}_${unit_id}_${toMonthFirstDay(date)}_${zipIds.length}_${isReturn ? "returns" : "forms"}.zip`;
    setZipBusy(true);
    setLoading(true);
    try {
      const response = await dispatch(Generated_forms_downloadZip(zipIds, zipName));
      if (response.success) showSnackbar(response.message, "success");
      else {
        showSnackbar(
          isClient ? clientDownloadErrorMessage(response.message) : (response.message || "Download failed"),
          isClient ? "warning" : "error"
        );
      }
    } catch {
      showSnackbar("An error occurred", "error");
    } finally {
      setZipBusy(false);
      setLoading(false);
    }
  };

  const handleViewPdfClick = async (row) => {
    const title = row.form_type || "Generated form";
    setPreviewTitle(title);
    setPreviewUnavailable(false);
    if (previewPdfUrl && String(previewPdfUrl).startsWith("blob:")) {
      URL.revokeObjectURL(previewPdfUrl);
    }
    setPreviewPdfUrl(null);
    setLoading(true);
    try {
      const response = row.id
        ? await dispatch(Generated_forms_viewById(row.id))
        : await dispatch(
            Generated_forms_viewDataInput({
              client_id: row.client_id,
              unit_id: row.unit_id,
              date: toMonthFirstDay(row.current_month),
              form_type: row.form_type,
              format: "pdf",
            })
          );
      if (response.success && response.blob) {
        const named = new File(
          [response.blob],
          `${String(title).replace(/_/g, " ")}.pdf`,
          { type: "application/pdf" }
        );
        setPreviewPdfUrl(URL.createObjectURL(named));
      } else {
        setPreviewUnavailable(true);
      }
    } catch {
      setPreviewUnavailable(true);
    }
  };

  const handleViewExcelClick = async (row) => {
    setLoading(true);
    try {
      const response = await dispatch(
        Generated_forms_viewDataInput({
          client_id: row.client_id,
          unit_id: row.unit_id,
          date: toMonthFirstDay(row.current_month),
          form_type: row.form_type,
          format: "excel",
        })
      );
      if (response.success && response.blob) {
        const url = URL.createObjectURL(
          new Blob([response.blob], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = `${row.client_id}_${row.unit_id}_${row.form_type}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        showSnackbar("Excel file downloaded", "success");
      } else {
        showSnackbar(response.message || "Excel view failed", "warning");
      }
    } catch {
      showSnackbar("An error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const handledeleteClickOpenalert = async (id) => {
    setLoading(true);
    try {
      const response = await dispatch(deleteGeneratedFormById(id));
      if (response.success) {
        showSnackbar(response.message, "success");
        await reloadList();
      } else {
        showSnackbar(response.message, "warning");
      }
    } catch {
      showSnackbar("An error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseClick = (row) => {
    setReleaseRows([row]);
    setOpenReleaseAlert(true);
  };

  const handleBulkRelease = (ids) => {
    const selected = getData.filter((row) => ids.map(String).includes(String(row.id)));
    if (!selected.length) {
      showSnackbar(isReturn ? "Select returns to release." : "Select forms to release.", "warning");
      return;
    }
    setReleaseRows(selected);
    setOpenReleaseAlert(true);
  };

  const handleReleaseCloseAlert = () => {
    setOpenReleaseAlert(false);
    setReleaseRows([]);
  };

  const handleReleaseConfirmed = async () => {
    const rows = releaseRows.filter((row) => row?.id);
    if (!rows.length) {
      handleReleaseCloseAlert();
      return;
    }
    setOpenReleaseAlert(false);
    setLoading(true);
    try {
      if (rows.length === 1) {
        const response = await dispatch(releaseGeneratedFormById(rows[0].id));
        if (response.success) {
          showSnackbar(response.message, "success");
          await reloadList();
        } else {
          showSnackbar(response.message || "Release failed", "warning");
        }
      } else {
        const response = await dispatch(releaseGeneratedFormsBulk(rows.map((row) => row.id)));
        if (response.success) {
          showSnackbar(response.message, "success");
          await reloadList();
        } else {
          showSnackbar(response.message || "Release failed", "warning");
        }
      }
    } catch {
      showSnackbar("An error occurred", "error");
    } finally {
      setReleaseRows([]);
      setLoading(false);
    }
  };

  const closePdfPreview = () => {
    if (previewPdfUrl && String(previewPdfUrl).startsWith("blob:")) {
      URL.revokeObjectURL(previewPdfUrl);
    }
    setPreviewPdfUrl(null);
    setPreviewUnavailable(false);
    setPreviewTitle("");
    setLoading(false);
  };

  const releaseLabel = releaseRows.length === 1
    ? releaseRows[0]?.form_type || (isReturn ? "this return" : "this form")
    : `${releaseRows.length} selected ${isReturn ? "returns" : "forms"}`;

  return (
    <div className="container-fluid generated-forms-page generated-forms-list-page">
      <div className="card generated-forms-card">
        <div className="card-body">
          <div className="generated-forms-list-top">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm generated-forms-list-back"
              onClick={() => navigate(isReturn ? "/generated-form?kind=return" : "/generated-form")}
            >
              Back
            </button>
          </div>
          <div className="generated-forms-filter-row">
            <div className="generated-forms-org-branch-row">
              <div className="generated-forms-field">
                <label className="form-label" htmlFor="list-org-select">Organization</label>
                <select id="list-org-select" className="form-select" value={client_id} disabled>
                  <option value={client_id}>{orgLabel}</option>
                </select>
              </div>
              <div className="generated-forms-field">
                <label className="form-label" htmlFor="list-branch-select">Branch</label>
                <select
                  id="list-branch-select"
                  className="form-select"
                  value={unit_id}
                  onChange={(e) => handleBranchChange(e.target.value)}
                >
                  {unitOptions.some((unit) => idsMatch(unit.unit_id, unit_id)) ? null : (
                    unit_id ? <option value={unit_id}>{unit_id}</option> : null
                  )}
                  {unitOptions.map((unit) => (
                    <option key={unit.unit_id} value={unit.unit_id}>
                      {branchOptionLabel(unit)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="generated-forms-year-month-row">
              <div className="generated-forms-field">
                <label className="form-label">Year</label>
                <select
                  className="form-select"
                  value={selectedYear}
                  onChange={(e) => handleYearChange(e.target.value)}
                >
                  {yearOptions.includes(selectedYear) || !selectedYear ? null : (
                    <option value={selectedYear}>{selectedYear}</option>
                  )}
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div className="generated-forms-field">
                <label className="form-label">Month</label>
                <select
                  className="form-select"
                  value={selectedMonth}
                  onChange={(e) => handleMonthChange(e.target.value)}
                  disabled={!selectedYear}
                >
                  {monthOptions.includes(selectedMonth) || !selectedMonth ? null : (
                    <option value={selectedMonth}>
                      {MONTH_NAMES[parseInt(selectedMonth, 10) - 1] || selectedMonth}
                    </option>
                  )}
                  {monthOptions.map((mm) => (
                    <option key={mm} value={mm}>
                      {MONTH_NAMES[parseInt(mm, 10) - 1]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <GenerateFormModel
        header={header}
        mainIcon={mainicon}
        title={isReturn ? "Generated Return" : "Generated Form"}
        data={getData}
        date={date}
        handledeleteClickOpenalert={handledeleteClickOpenalert}
        setLoading={setLoading}
        showSnackbar={showSnackbar}
        handleDownloadClick={handleDownloadClick}
        handleZipDownload={handleZipDownload}
        handleViewPdfClick={handleViewPdfClick}
        handleViewExcelClick={handleViewExcelClick}
        handleReleaseClick={isClient ? undefined : handleReleaseClick}
        handleBulkRelease={isClient ? undefined : handleBulkRelease}
        zipBusy={zipBusy}
        itemNoun={itemNoun}
        createOrfilterComp={<></>}
      />

      {(previewPdfUrl || previewUnavailable) && (
        <GeneratedFormPdfPreview
          pdfUrl={previewPdfUrl}
          title={previewTitle}
          fileName={previewTitle}
          unavailable={previewUnavailable}
          onReady={() => setLoading(false)}
          onClose={closePdfPreview}
        />
      )}

      {!isClient && (
        <Dialog
          open={openReleaseAlert}
          TransitionComponent={Transition}
          keepMounted
          aria-describedby="release-dialog-description"
          onClose={handleReleaseCloseAlert}
        >
          <DialogTitle>{"Are you sure?"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="release-dialog-description">
              Are you sure you want to release{" "}
              <strong>{releaseLabel}</strong>
              {" "}of{" "}
              <strong>{formatMonthLabel(releaseRows[0]?.current_month || date)}</strong>
              {" "}to{" "}
              <strong>{orgName || client_id || "the client"}</strong>?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleReleaseCloseAlert}>No</Button>
            <Button onClick={handleReleaseConfirmed}>Yes</Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}

export default GeneratedFormList;
